import React, { useState, useEffect, useRef, useCallback } from 'react';
import { fetchJson } from '../utils/fetchJson';
import { formatTime } from '../utils/formatTime';

const severityColors = {
  critical: { dot: 'var(--danger)', text: 'var(--danger)', bg: 'rgba(248,113,113,0.12)' },
  high: { dot: '#ec4899', text: '#f9a8d4', bg: 'rgba(236,72,153,0.12)' },
  warning: { dot: 'var(--warning)', text: 'var(--warning)', bg: 'rgba(251,191,36,0.12)' },
  warn: { dot: 'var(--warning)', text: 'var(--warning)', bg: 'rgba(251,191,36,0.12)' },
  medium: { dot: 'var(--accent-2)', text: 'var(--accent-2)', bg: 'rgba(139,92,246,0.12)' },
  low: { dot: 'var(--success)', text: 'var(--success)', bg: 'rgba(52,211,153,0.12)' },
  info: { dot: 'var(--info)', text: 'var(--info)', bg: 'rgba(96,165,250,0.12)' },
};

const POLL_CLOSED_MS = 5000;
const POLL_OPEN_MS = 2000;

export default function NotificationBell() {
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [unseen, setUnseen] = useState(0);
  const ref = useRef(null);
  const seenNames = useRef(new Set());
  const lastMarkReadAt = useRef(0);
  const pollInterval = useRef(null);
  const animatingIds = useRef(new Set());

  const fetchAlerts = useCallback(async () => {
    const data = await fetchJson('/api/alerts?source=synoti_system').catch(() => null);
    if (!data?.alerts) return;

    const list = data.alerts;
    const active = list.filter(a => a.status !== 'acknowledged' && a.status !== 'resolved');

    setAlerts(prev => {
      const prevIds = new Set(prev.map(a => a.name));
      const newOnes = active.filter(a => !prevIds.has(a.name) && !seenNames.current.has(a.name));
      if (newOnes.length > 0) {
        newOnes.forEach(a => {
          seenNames.current.add(a.name);
          animatingIds.current.add(a.name);
          setTimeout(() => animatingIds.current.delete(a.name), 3000);
        });
        const now = Date.now();
        if (now - lastMarkReadAt.current > 30000) {
          setUnseen(u => u + newOnes.length);
        }
      }
      return active;
    });
  }, []);

  useEffect(() => {
    fetchAlerts();
    pollInterval.current = setInterval(fetchAlerts, POLL_CLOSED_MS);
    return () => clearInterval(pollInterval.current);
  }, [fetchAlerts]);

  useEffect(() => {
    if (open) {
      clearInterval(pollInterval.current);
      pollInterval.current = setInterval(fetchAlerts, POLL_OPEN_MS);
    } else {
      clearInterval(pollInterval.current);
      pollInterval.current = setInterval(fetchAlerts, POLL_CLOSED_MS);
    }
    return () => clearInterval(pollInterval.current);
  }, [open, fetchAlerts]);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleOpen = () => {
    setOpen(!open);
    if (!open) setUnseen(0);
  };

  const markAllAsRead = async () => {
    setUnseen(0);
    lastMarkReadAt.current = Date.now();
    const names = alerts.map(a => a.name);
    names.forEach(n => seenNames.current.add(n));

    setAlerts(prev => prev.map(a => ({
      ...a,
      status: 'acknowledged',
      acknowledged_at: new Date().toISOString(),
    })));

    await fetchJson('/api/alerts/acknowledge-all', { method: 'POST' }).catch(() => {});
  };

  const goToAlerts = () => {
    setOpen(false);
    sessionStorage.setItem('incidents_tab', 'alerts');
    if (window.location.hash === '#incidents') {
      window.dispatchEvent(new CustomEvent('switch-tab', { detail: 'alerts' }));
    } else {
      window.location.hash = 'incidents';
    }
  };

  const activeCount = alerts.filter(a => a.status !== 'acknowledged' && a.status !== 'resolved').length;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        className="relative"
        style={{ padding: 8, borderRadius: 10, color: 'var(--text-2)' }}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {activeCount > 0 && (
          <span className="absolute inline-flex items-center justify-center" style={{ top: -2, right: -2, minWidth: 18, height: 18, padding: '0 4px', borderRadius: '50%', fontSize: 10, fontWeight: 700, background: 'var(--danger)', color: '#fff' }}>
            {activeCount > 99 ? '99+' : activeCount}
          </span>
        )}
        {unseen > 0 && (
          <span className="absolute animate-ping" style={{ top: 4, right: 4, width: 10, height: 10, background: 'var(--danger)', borderRadius: '50%' }} />
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 overflow-hidden w-80 sm:w-96" style={{ marginTop: 8, maxHeight: '70vh', background: 'var(--surface)', borderRadius: 12, boxShadow: 'var(--shadow)', border: '1px solid var(--glass-border)' }}>
          <div className="flex items-center justify-between" style={{ padding: 12, borderBottom: '1px solid var(--glass-border)' }}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>System Notifications</h3>
            <div className="flex items-center gap-2">
              {activeCount > 0 ? (
                <>
                  <button
                    onClick={markAllAsRead}
                    style={{ fontSize: 12, color: 'var(--text-3)' }}
                  >
                    Mark all as read
                  </button>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>|</span>
                  <span style={{ fontSize: 12, color: 'var(--text-3)' }}>{activeCount} active</span>
                </>
              ) : (
                <span style={{ fontSize: 12, color: 'var(--text-3)' }}>No active alerts</span>
              )}
              <button onClick={goToAlerts} style={{ fontSize: 12, color: 'var(--accent-1)', marginLeft: 4 }}>
                View All
              </button>
            </div>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: '55vh' }}>
            {alerts.length === 0 ? (
              <div className="text-center" style={{ padding: 32 }}>
                <svg className="mx-auto" style={{ width: 40, height: 40, marginBottom: 12, color: 'var(--text-3)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                  <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                </svg>
                <p style={{ fontSize: 14, color: 'var(--text-3)' }}>All clear — no system alerts</p>
              </div>
            ) : (
              alerts.map((alert, idx) => {
                const sev = severityColors[alert.severity] || severityColors.medium;
                const isAck = alert.status === 'acknowledged';
                const isNew = animatingIds.current.has(alert.name);
                return (
                  <div
                    key={idx}
                    onClick={goToAlerts}
                    className={`cursor-pointer ${isNew ? 'animate-slideIn' : ''} ${isAck ? 'opacity-60' : ''}`}
                    style={{ padding: 12, borderBottom: idx === alerts.length - 1 ? 'none' : '1px solid var(--glass-border)' }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`flex-shrink-0 ${!isAck && alert.firing ? 'animate-pulse' : ''}`} style={{ width: 8, height: 8, borderRadius: '50%', marginTop: 6, background: sev.dot }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="truncate" style={{ fontSize: 14, fontWeight: 500, color: isAck ? 'var(--text-3)' : 'var(--text-1)' }}>
                            {alert.name}
                          </span>
                          <span className="flex-shrink-0" style={{ padding: '2px 6px', borderRadius: 4, fontSize: 10, fontWeight: 500, background: sev.bg, color: sev.text }}>
                            {alert.severity}
                          </span>
                          {isAck && (
                            <span className="flex-shrink-0" style={{ fontSize: 10, color: 'var(--accent-3)' }}>✓</span>
                          )}
                        </div>
                        {alert.description && (
                          <p className="line-clamp-2" style={{ fontSize: 12, color: 'var(--text-2)', marginTop: 2 }}>{alert.description}</p>
                        )}
                        {alert.instance && (
                          <p style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>{alert.instance}</p>
                        )}
                        {alert.fired_at && (
                          <p style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                            {formatTime(alert.fired_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          <style>{`
            @keyframes slideIn {
              from { opacity: 0; transform: translateX(-10px); background: rgba(139, 92, 246, 0.08); }
              to { opacity: 1; transform: translateX(0); background: transparent; }
            }
            .animate-slideIn { animation: slideIn 0.4s ease-out; }
          `}</style>
        </div>
      )}
    </div>
  );
}
